import React, { useState } from 'react';
import { Icon } from '@kaizen/component-library';
import { Heading, Paragraph } from '@kaizen/typography';
import classNames from 'classnames';
import styles from './WelcomeScreenMessage.scss';
import { useData } from 'ca-welcome-screen/src/hooks';
import { useMediaQuery, useRTL } from 'ca-hooks';
import chevronDown from '@kaizen/component-library/icons/chevron-down.icon.svg';
import chevronUp from '@kaizen/component-library/icons/chevron-up.icon.svg';
import { QuoteGraphic } from './QuoteGraphic';
import { FormattedMessage, useIntl } from '@cultureamp/i18n-react-intl';

const NUM_OF_DISPLAY_CHARACTERS_LIMIT = 110;

export const WelcomeScreenMessage = () => {
  const { welcome_content, account_name, welcome_screen_locale } = useData();
  const isRTL = useRTL(welcome_screen_locale.code);
  const isDesktop = useMediaQuery('(min-width:798px)');
  const isMobile = !isDesktop;
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const tooMuchText =
    welcome_content && welcome_content.length > NUM_OF_DISPLAY_CHARACTERS_LIMIT;

  const onShowMore = () => setIsExpanded(!isExpanded);
  const { formatMessage } = useIntl();

  const showMore: string = formatMessage({
    id: 'welcome_screen.show_more',
    defaultMessage: 'Show more',
  });
  const showLess: string = formatMessage({
    id: 'welcome_screen.show_less',
    defaultMessage: 'Show less',
  });

  const replaceLinkTarget = (text: string | null) => {
    if (!text) {
      return '';
    }
    const modalContentReplaced = text.replaceAll('<a', '<a target="_blank"');
    return modalContentReplaced;
  };

  return (
    <div className={styles.WelcomeScreenMessage}>
      <div
        className={classNames(
          styles.WelcomeScreenMessageWrapper,
          isMobile ? styles.Mobile : '',
          isExpanded ? styles.Expanded : ''
        )}
      >
        <div className={styles.QuoteGraphic}>
          <QuoteGraphic />
        </div>
        <div className={styles.Heading} dir={isRTL ? 'rtl' : 'ltr'}>
          <Heading variant="heading-3" tag="h2">
            <FormattedMessage
              id="welcome_screen.message_from"
              defaultMessage="A message from {account_name}"
              values={{ account_name }}
            />
          </Heading>
        </div>
        <Paragraph
          variant="intro-lede"
          tag="div"
          classNameOverride={classNames(
            styles.MessageTextContainer,
            isMobile ? styles.Mobile : '',
            isExpanded ? styles.Expanded : ''
          )}
        >
          <div
            className={styles.MessageText}
            dir={isRTL ? 'rtl' : 'ltr'}
            dangerouslySetInnerHTML={{
              __html: replaceLinkTarget(welcome_content),
            }}
          />
        </Paragraph>
        <div
          className={classNames(
            styles.FadeLayer,
            isExpanded ? styles.Expanded : '',
            !tooMuchText ? styles.hideFade : ''
          )}
        >
          {tooMuchText && isMobile && (
            <button className={styles.ShowMoreButton} onClick={onShowMore}>
              <span className={styles.ShowMoreButtonText}>
                {!isExpanded ? showMore : showLess}
              </span>
              <span className={styles.ShowMoreButtonIcon}>
                <Icon
                  icon={!isExpanded ? chevronDown : chevronUp}
                  role="presentation"
                />
              </span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
